<script setup lang="ts">
import { useClient } from '@/stores/client';
import { Button } from '@/components/ui/button';
import AppFeedNavigationItem from '@/components/feed/AppFeedNavigationItem.vue';

type NavigationItem = {
  id: string,
  label: string,
  icon: string,
  to: string,
  count?: number,
};

const route = useRoute();
const client = useClient();
const favoritesCount = useState<number>('favoritesCount', () => 0);

const currentYear = new Date().getFullYear();

const items = computed<NavigationItem[]>(() => {
  const companyId = client.getActiveCompany?.id;

  return [
    {
      id: 'feed',
      label: 'Лента',
      icon: 'tabler:layout-grid-filled',
      to: '/feed',
    },
    {
      id: 'favorites',
      label: 'Избранное',
      icon: 'tabler:heart-filled',
      to: '/favorites',
      count: favoritesCount.value,
    },
    {
      id: 'chat',
      label: 'Чат',
      icon: 'tabler:message-filled',
      to: '/chats',
    },
    {
      id: 'responses',
      label: 'Отклики',
      icon: 'tabler:target-arrow',
      to: companyId ? `/company/${companyId}/vacancies` : '/applicant/responses',
    },
    {
      id: 'profile',
      label: 'Профиль',
      icon: 'tabler:user-filled',
      to: '/profile',
    },
  ];
});

const isSearchActive = computed(() => route.path.startsWith('/search'));

function isActive(item: NavigationItem): boolean {
  if (item.id === 'feed') {
    return route.path.startsWith('/feed');
  }
  if (item.id === 'profile') {
    return route.path === item.to
      || route.path.startsWith(`${item.to}/`)
      || route.path.startsWith('/notifications')
      || route.path.startsWith('/profile/settings');
  }
  return route.path === item.to || route.path.startsWith(`${item.to}/`);
}
</script>

<template>
  <aside class="hidden min-h-full w-56 flex-col items-stretch bg-background py-10 pr-6 pb-8 pl-5 md:flex min-[1100px]:w-64 min-[1100px]:pl-8">
    <Button as-child variant="outline" class="mb-5 flex h-[4.5rem] w-[11.5rem] items-center justify-center overflow-hidden rounded-full border-2 border-border text-foreground min-[1100px]:w-52 [&_svg]:!h-8 [&_svg]:!w-auto [&_svg]:max-w-[7.75rem] [&_svg]:shrink-0">
      <NuxtLink to="/feed" aria-label="Sobes — лента">
        <Icon name="local:logo-full" aria-hidden="true" />
      </NuxtLink>
    </Button>

    <AppFeedNavigationItem to="/search" label="Поиск" icon="tabler:search" :active="isSearchActive" />

    <nav class="flex flex-col gap-1" aria-label="Основная навигация">
      <AppFeedNavigationItem
        v-for="item in items"
        :key="item.id"
        v-bind="item"
        :active="isActive(item)"
      />
    </nav>

    <div class="min-h-8 flex-auto" />

    <footer class="flex flex-col gap-3.5 border-t border-border px-1 pt-5">
      <div class="flex items-center gap-2.5">
        <a
          href="https://t.me/sobes_ru"
          target="_blank"
          rel="noopener"
          aria-label="Sobes в Telegram"
          class="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-transform duration-150 hover:scale-[1.08] focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-ring [&_svg]:size-full [&_svg]:scale-[1.2]"
        >
          <Icon name="local:telegram" aria-hidden="true" />
        </a>
        <a
          href="https://vk.com/sobes_ru"
          target="_blank"
          rel="noopener"
          aria-label="Sobes во ВКонтакте"
          class="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground transition-transform duration-150 hover:scale-[1.08] focus-visible:outline-[3px] focus-visible:outline-offset-2 focus-visible:outline-ring [&_svg]:size-full"
        >
          <Icon name="local:vk" aria-hidden="true" />
        </a>
      </div>
      <nav class="flex flex-col gap-1.5" aria-label="Документы">
        <NuxtLink to="#" class="text-xs leading-[1.4] text-muted-foreground no-underline transition-colors hover:text-primary hover:underline">
          Политика конфиденциальности
        </NuxtLink>
        <NuxtLink to="#" class="text-xs leading-[1.4] text-muted-foreground no-underline transition-colors hover:text-primary hover:underline">
          Пользовательское соглашение
        </NuxtLink>
      </nav>
      <p class="m-0 text-xs leading-[1.5] text-muted-foreground">
        Sobes.ru — © {{ currentYear }}<br>
        Все права защищены
      </p>
    </footer>
  </aside>
</template>
